import React, { useEffect, useState, Fragment, useReducer } from "react";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import { hashHistory } from 'react-router';
import SqftFeaturesPopupApplyButton from "./sqftFeaturesPopupApplyButton";
import { changeSqftFeaturesPopup } from "../../../actions/sqftFunctions";
import SqftFeaturesPopupBody from "./sqftFeaturesPopupBody";
import {
  getOversizedFeatureGroup,
  getParsedSqftData,
  getSimilarFeatureGroup,
  getTallCabinetInteriorFeatureGroups
} from "./parseSqftData";
import { removeFromCart, setCameFromPage } from "../../../actions/actionFunctions";
import AllFeaturesChecker from "../../visComponents/allFeaturesChecker";
import MaterialSpinner from "../../ui/materialSpinner";
import workspace from "../../sqft/components/canvas/core/workspace";
import useSetSqftConflictItems from "./useSetSqftConflictItems";
import {handleOpenConflictPopups} from "../../../misc/conflictPopups";
import {getCurrentLayout} from "../../appLayout";

const toFeatureGroupSubPart = (featureGroup) => {
  return featureGroup ? {part: featureGroup} : null;
};

const hasReceivedAllFeatures = (subPart) => {
  return !subPart || !subPart.part || subPart.part.allFeaturesReceived;
};

const SqftFeaturesPopup = (props) => {
  const { changeSqftFeaturesPopup, sqftFeaturesPopup, intl, fabricatorData, sqftFromFeature, cartItems, setCameFromPage,
    stonePricing, quotes, removeFromCart, isModalFixOpen} = props;
  const { opened, sqftPassedData } = sqftFeaturesPopup;
  const [parentsLists, setParentsLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const edgesFeatureSubPart = opened ? getSimilarFeatureGroup("edges") : null;
  const oversizedFeatureGroup = opened ? getOversizedFeatureGroupFromQuotes() : null;
  const tallCabinetInteriorFeatureGroups = opened ? getTallCabinetInteriorFeatureGroupsFromQuotes() : [];
  const oversizedFeatureSubPart = toFeatureGroupSubPart(oversizedFeatureGroup);
  const tallCabinetInteriorSubParts = tallCabinetInteriorFeatureGroups.map(toFeatureGroupSubPart);
  const requiredFeatureSubParts = [
    edgesFeatureSubPart,
    oversizedFeatureSubPart,
    ...tallCabinetInteriorSubParts,
  ];
  const featureGroupsToReceive = requiredFeatureSubParts.filter(subPart => {
    return !hasReceivedAllFeatures(subPart);
  });
  const edgesReceived = hasReceivedAllFeatures(edgesFeatureSubPart);
  const oversizedReceived = hasReceivedAllFeatures(oversizedFeatureSubPart);
  const tallCabinetInteriorReceived = tallCabinetInteriorSubParts.every(hasReceivedAllFeatures);
  const readyToRebuild = opened && sqftPassedData && workspace.intl && featureGroupsToReceive.length === 0;

  useEffect(() => {
    if (readyToRebuild) {
      setLoading(true);
      removeDeletedInSQFTFeatures();
      rebuildQuoteLists();
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqftPassedData, edgesReceived, oversizedReceived, tallCabinetInteriorReceived]);

  useEffect(() => {
    // rebuild quotes list to get temp cartItems and correct check for the conflict steps
    if (readyToRebuild || isModalFixOpen) {
      rebuildQuoteLists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  const rebuildQuoteLists = () => {
    const quoteLists = quotes.map((quote, quoteIndex) => {
      return {
        quoteIndex,
        quote,
        qtCatName: quote.qtCatName,
        ...getParsedSqftData(
          quote.sqFtCalcData,
          sqftFeaturesPopup.previouslyAddedSqftFeatures,
          intl,
          fabricatorData,
          cartItems,
          sqftFromFeature,
          quoteIndex,
          quote.room,
          stonePricing,
        )
      };
    });
    setParentsLists([...quoteLists]);
  };

  useSetSqftConflictItems({
    shouldRebuild: opened || isModalFixOpen,
    parentsLists,
  });

  function getOversizedFeatureGroupFromQuotes() {
    for (const quote of quotes) {
      const layout = getCurrentLayout(quote.room);
      const featureGroup = getOversizedFeatureGroup(quote.sqFtCalcData, layout, quote.room);
      if (featureGroup) {
        return featureGroup;
      }
    }
    return null;
  }

  function getTallCabinetInteriorFeatureGroupsFromQuotes() {
    return quotes.reduce((featureGroups, quote) => {
      const layout = getCurrentLayout(quote.room);
      const quoteFeatureGroups = getTallCabinetInteriorFeatureGroups(quote.sqFtCalcData, layout);

      quoteFeatureGroups.forEach((featureGroup) => {
        if (!featureGroups.includes(featureGroup)) {
          featureGroups.push(featureGroup);
        }
      });

      return featureGroups;
    }, []);
  }

  if (!opened) {
    return null;
  }

  const removeDeletedInSQFTFeatures = () => {
    if (sqftPassedData.removedFeatures) {
      const removedFeatures = cartItems.filter(cartItem => {
        return sqftPassedData.removedFeatures.includes(cartItem.sqftId);
      });
      removedFeatures.forEach(cartItem => removeFromCart(cartItem));
    }
  };

  const closePopup = () => {
    changeSqftFeaturesPopup({
      opened: false,
      sqftPassedData: null
    });
  };

  const backButtonClick = () => {
    setCameFromPage("materials");
    hashHistory.push("sqft");
    closePopup();
    handleOpenConflictPopups();
  };

  return (
    <Fragment>
      <div className="ui-popup__header">
          <span className="ui-popup__header-title">
            <FormattedMessage id="sqftFeaturesPopup.title" />
          </span>
        <button className="ui-close contract-popup__close" onClick={closePopup} />
      </div>
      {featureGroupsToReceive.length ? (
        <div className="ui-popup__body ui-popup__body--relative">
          <AllFeaturesChecker subParts={featureGroupsToReceive}
                              callBack={() => forceUpdate(1)}
                              loaderOnly={true} />
        </div>
      ) : (
        loading ? (
          <MaterialSpinner />
        ) : (
          <SqftFeaturesPopupBody parentsLists={parentsLists}
                                 setParentsLists={setParentsLists}
                                 rebuildQuoteLists={rebuildQuoteLists} />
        )
      )}
      <div className="ui-popup__footer">
        <div className="ui-popup__footer-right">
          <button className="ui-button2 ui-button2--blue" onClick={backButtonClick}>
            <FormattedMessage id="assignPopup.backButton" />
          </button>
          <SqftFeaturesPopupApplyButton parentsLists={parentsLists} closePopup={closePopup} />
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (store) => {
  const { sqftFeaturesPopup, sqftFromFeature } = store.sqftState;
  const { cartItems } = store.cartState;
  const { stonePricing } = store.serverState;
  const { isModalFixOpen } = store.sqftModalFixState;
  return {
    sqftFeaturesPopup,
    sqftFromFeature,
    fabricatorData: store.fabricatorState,
    cartItems,
    activeQuoteIndex: store.quotesState.activeQuoteIndex,
    stonePricing,
    quotes: store.quotesState.quotes,
    fabricatorSettings: store.fabricatorState.settings,
    isModalFixOpen,
  };
};
const mapDispatchToProps = {
  changeSqftFeaturesPopup,
  setCameFromPage,
  removeFromCart,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftFeaturesPopup)
);
